'use client';

import { createContext, startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './LocaleRuntime.module.scss';

import type { AppDictionary } from '@/features/i18n/dictionaries';
import type { LandingContent } from '@/features/i18n/landingContent';
import {
    getIntlLocale,
    getLocaleDirection,
    LOCALE_COOKIE_KEY,
    LOCALE_STORAGE_KEY,
    normalizeLocale,
    SUPPORTED_LOCALES,
    type SupportedLocale,
} from '@/features/i18n/logic';
import { type RuntimeI18nPayload, getRuntimeI18nPayloadScope } from '@/features/i18n/payload';
import { LOGIN_I18N_SCOPE, type I18nPayloadScope } from '@/features/i18n/payloadScope';
import { getDocumentOrNull, getWindowOrNull } from '@/lib/browser/runtime';

const getStoredLocale = (fallbackLocale: SupportedLocale): SupportedLocale => {
    const browserWindow = getWindowOrNull();
    if (!browserWindow) {
        return fallbackLocale;
    }

    try {
        return normalizeLocale(browserWindow.localStorage.getItem(LOCALE_STORAGE_KEY));
    } catch {
        return fallbackLocale;
    }
};

type LocaleRuntimeProps = {
    initialPayload: RuntimeI18nPayload;
    children: React.ReactNode;
};

export type I18nContextValue = {
    locale: SupportedLocale;
    dictionary: AppDictionary;
    landingContent: LandingContent;
    direction: 'ltr' | 'rtl';
    intlLocale: string;
    localeOptions: Array<{ value: SupportedLocale; label: string }>;
    setLocale: (locale: SupportedLocale) => void;
};

export const I18nContext = createContext<I18nContextValue | null>(null);

const getLocalePayloadHref = (locale: SupportedLocale, scope: I18nPayloadScope) => {
    const searchParams = new URLSearchParams({ locale });
    if (scope === LOGIN_I18N_SCOPE) {
        searchParams.set('scope', LOGIN_I18N_SCOPE);
    }

    return `/i18n?${searchParams.toString()}`;
};

const fetchLocalePayload = async (locale: SupportedLocale, scope: I18nPayloadScope): Promise<RuntimeI18nPayload> => {
    const browserWindow = getWindowOrNull();
    const href = getLocalePayloadHref(locale, scope);
    const url = browserWindow ? new URL(href, browserWindow.location.origin).toString() : `http://127.0.0.1${href}`;

    const response = await fetch(url, {
        cache: 'force-cache',
        credentials: 'same-origin',
    });

    if (!response.ok) {
        throw new Error(`Failed to load locale payload for ${locale}`);
    }

    return (await response.json()) as RuntimeI18nPayload;
};

export const LocaleRuntime = ({ initialPayload, children }: LocaleRuntimeProps) => {
    const payloadScope = getRuntimeI18nPayloadScope(initialPayload);
    const [payload, setPayload] = useState<RuntimeI18nPayload>(initialPayload);
    const requestIdRef = useRef(0);

    const applyLocale = useCallback(
        async (nextLocale: SupportedLocale) => {
            if (nextLocale === payload.locale) {
                return;
            }

            const requestId = requestIdRef.current + 1;
            requestIdRef.current = requestId;

            const nextPayload = await fetchLocalePayload(nextLocale, payloadScope);

            if (requestIdRef.current !== requestId) {
                return;
            }

            startTransition(() => {
                setPayload(nextPayload);
            });
        },
        [payload.locale, payloadScope]
    );

    useEffect(() => {
        const hydratedLocale = getStoredLocale(initialPayload.locale);

        if (hydratedLocale && hydratedLocale !== initialPayload.locale) {
            void applyLocale(hydratedLocale);
        }
    }, [applyLocale, initialPayload]);

    useEffect(() => {
        const browserDocument = getDocumentOrNull();
        const browserWindow = getWindowOrNull();
        if (!browserDocument || !browserWindow) {
            return;
        }

        browserDocument.documentElement.lang = payload.locale;
        browserDocument.documentElement.dir = payload.locale === 'ar' ? 'rtl' : 'ltr';
        browserDocument.documentElement.dataset.locale = payload.locale;
        browserDocument.body.dir = payload.locale === 'ar' ? 'rtl' : 'ltr';

        try {
            browserWindow.localStorage.setItem(LOCALE_STORAGE_KEY, payload.locale);
        } catch {
            // Best effort only.
        }

        browserDocument.cookie = `${LOCALE_COOKIE_KEY}=${encodeURIComponent(payload.locale)}; path=/; max-age=31536000; samesite=lax`;
    }, [payload.locale]);

    const contextValue = useMemo<I18nContextValue>(
        () => ({
            locale: payload.locale,
            dictionary: payload.dictionary as AppDictionary,
            landingContent: ('landingContent' in payload ? payload.landingContent : undefined) as LandingContent,
            direction: getLocaleDirection(payload.locale),
            intlLocale: getIntlLocale(payload.locale),
            localeOptions: SUPPORTED_LOCALES.map((value) => ({
                value,
                label: payload.dictionary.languages[value],
            })),
            setLocale: (nextLocale: SupportedLocale) => {
                void applyLocale(nextLocale);
            },
        }),
        [applyLocale, payload]
    );

    return <I18nContext.Provider value={contextValue}>{children}</I18nContext.Provider>;
};
